import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../../design/Icon';
import { Alert, Button, Spinner, useToast } from '../../components/shared';
import { useAuth } from '../../contexts/AuthContext';
import { saveBlob } from '../../services/exportService';
import adminAPI, { errorMessage } from '../../services/adminApi';
import { combineSummaries, csvHeader, csvRowCount, importButtonLabel, importEntities, lower, plural, summarizeReport } from './adminModel';
import { useTerms } from '../../layout/ShellStatus';

// The preview survives a reload of the page (sessionStorage), so an administrator can come back to it.
export const IMPORT_STATE_KEY = 'vl-admin-import';
const MAX_KEPT_CSV = 2 * 1024 * 1024;

const readKept = () => {
  try {
    const raw = window.sessionStorage.getItem(IMPORT_STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const keep = (state) => {
  try {
    if (!state) window.sessionStorage.removeItem(IMPORT_STATE_KEY);
    else window.sessionStorage.setItem(IMPORT_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    // Storage full or blocked: the preview lasts for this page view only.
  }
};

const splitTags = (value) =>
  String(value || '')
    .split(/[;,]/)
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * Import tab: choose a file (or the Leland starter data), preview the merge as a dry run, then apply it.
 * Never applies without the explicit Import button (dryRun=false). `only` fixes the entity (the Directory page imports only `directory`).
 */
export function CsvImport({ only = null, visible = true, levelName, levelPlural, orgName, onApplied, onCancel, onPreview, openChooser }) {
  const toast = useToast();
  const { user } = useAuth();
  const terms = useTerms();
  const entities = importEntities(levelName, levelPlural, terms);
  const [entity, setEntity] = useState(only || 'communities');
  const [state, setState] = useState(() => {
    const kept = readKept();
    return only && kept && kept.entity !== only ? null : kept;
  });
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (openChooser && fileInput.current) fileInput.current.click();
  }, [openChooser]);

  const update = (next) => {
    setState(next);
    keep(next && next.csv && next.csv.length > MAX_KEPT_CSV ? { ...next, csv: null } : next);
  };

  const choose = () => fileInput.current && fileInput.current.click();

  const preview = async (file, forEntity) => {
    setBusy('preview');
    setProblem(null);
    try {
      const text = await file.text();
      const report = await adminAPI.importCsv(forEntity, file);
      if (onPreview) onPreview();
      update({
        kind: 'file',
        entity: forEntity,
        fileName: file.name,
        rowCount: csvRowCount(text),
        columns: csvHeader(text),
        uploadedBy: user?.displayName || user?.email || 'you',
        csv: text,
        reports: [{ entity: forEntity, report }],
      });
    } catch (err) {
      setProblem(errorMessage(err, "The file couldn't be read. Check that it is a CSV with the template's columns."));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const previewSeed = async () => {
    setBusy('preview');
    setProblem(null);
    try {
      const result = await adminAPI.seedLeland();
      update({
        kind: 'seed',
        entity: 'communities',
        fileName: `${orgName} starter data`,
        uploadedBy: user?.displayName || user?.email || 'you',
        reports: [
          { entity: 'hierarchy-nodes', report: result.hierarchyNodes },
          { entity: 'communities', report: result.communities },
          { entity: 'mappings', report: result.mappings },
        ],
        extras: { levelCreated: result.levelCreated, aiSettingsCreated: result.aiSettingsCreated },
      });
    } catch (err) {
      setProblem(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const downloadTemplate = async (forEntity) => {
    try {
      const text = await adminAPI.getTemplate(forEntity);
      saveBlob(new Blob([text], { type: 'text/csv;charset=utf-8' }), `${forEntity}-template.csv`);
    } catch (err) {
      toast(errorMessage(err), 'alert');
    }
  };

  const cancel = () => {
    update(null);
    setProblem(null);
    if (onCancel) onCancel();
  };

  const apply = async () => {
    if (!state) return;
    setBusy('apply');
    setProblem(null);
    try {
      let reports;
      if (state.kind === 'seed') {
        const result = await adminAPI.seedLeland({ dryRun: false });
        reports = [result.hierarchyNodes, result.communities, result.mappings];
      } else {
        if (!state.csv) throw new Error('missing file');
        const file = new File([state.csv], state.fileName, { type: 'text/csv' });
        reports = [await adminAPI.importCsv(state.entity, file, { dryRun: false })];
      }
      const totals = combineSummaries(reports.map(summarizeReport));
      update(null);
      toast(
        `Imported: ${totals.createdCount} created, ${totals.updatedCount} updated, ${totals.unchangedCount} unchanged.${totals.errorCount ? ` ${plural(totals.errorCount, 'row')} skipped.` : ''}`
      );
      if (onApplied) onApplied(state.entity);
    } catch (err) {
      setProblem(err.message === 'missing file' ? 'Choose the file again to import it.' : errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const fileInputElement = (
    <input
      ref={fileInput}
      type="file"
      accept=".csv,text/csv"
      hidden
      onChange={(e) => {
        const file = e.target.files && e.target.files[0];
        if (file) preview(file, state && state.kind === 'file' ? state.entity : entity);
      }}
    />
  );

  if (!visible) return fileInputElement;

  if (!state) {
    const showSeed = !only && /^leland\b/i.test(orgName || '');
    return (
      <div className="stack-lg">
        {fileInputElement}
        <section className="card">
          <div className="card-body row-wrap" style={{ gap: 16 }}>
            <FileBadge />
            <div className="grow">
              <div className="strong">Import from CSV</div>
              <div className="caption">{only ? 'Choose a file' : 'Choose what the file holds, then the file'}. You see what would change before anything is saved.</div>
            </div>
            {!only && (
              <select className="select" style={{ maxWidth: 260 }} value={entity} onChange={(e) => setEntity(e.target.value)} aria-label="What the file holds">
                {Object.entries(entities).map(([id, e]) => (
                  <option key={id} value={id}>
                    {e.label}
                  </option>
                ))}
              </select>
            )}
            <Button variant="primary" icon="upload" onClick={choose} disabled={busy != null}>
              Choose a file
            </Button>
            <Button variant="ghost" onClick={() => downloadTemplate(entity)}>
              Download template
            </Button>
          </div>
        </section>
        <div className="alert">
          <Icon name="info" />
          <div>{entities[entity].matching}</div>
        </div>
        {showSeed && (
          <section className="card">
            <div className="card-body row-wrap" style={{ gap: 16 }}>
              <div className="grow">
                <div className="strong">{orgName} starter data</div>
                <div className="caption">
                  The {lower(levelPlural)}, {terms.communitiesLower}, and queue mappings found during discovery, previewed through the same merge as a file.
                </div>
              </div>
              <Button onClick={previewSeed} disabled={busy != null}>
                Preview starter data
              </Button>
            </div>
          </section>
        )}
        {busy === 'preview' && <Spinner label="Checking the file…" />}
        {problem && <Alert tone="error">{problem}</Alert>}
      </div>
    );
  }

  const summaries = state.reports.map((r) => ({ ...r, summary: summarizeReport(r.report) }));
  const totals = combineSummaries(summaries.map((s) => s.summary));
  const meta =
    state.kind === 'seed'
      ? `${plural(summaries.reduce((n, s) => n + (s.report.rows || []).length, 0), 'row')} · ${summaries.map((s) => lower(entities[s.entity].label)).join(', ')} · previewed by ${state.uploadedBy} just now`
      : `${plural(state.rowCount || 0, 'row')} · columns: ${(state.columns || []).map(lower).join(', ')} · uploaded by ${state.uploadedBy} just now`;

  return (
    <div className="stack-lg" data-testid="csv-merge-preview">
      {fileInputElement}
      <section className="card">
        <div className="card-body row-wrap" style={{ gap: 16 }}>
          <FileBadge />
          <div className="grow">
            <div className="strong">{state.fileName}</div>
            <div className="caption">{meta}</div>
          </div>
          <button className="btn" type="button" onClick={state.kind === 'seed' ? cancel : choose} disabled={busy != null}>
            Choose a different file
          </button>
          <button className="btn btn-ghost" type="button" onClick={() => downloadTemplate(state.entity)}>
            Download template
          </button>
        </div>
      </section>
      <div className="alert">
        <Icon name="info" />
        <div>{entities[state.entity].matching}</div>
      </div>
      {problem && <Alert tone="error">{problem}</Alert>}
      {summaries.map((s) => (
        <ReportPreview key={s.entity} entity={entities[s.entity]} summary={s.summary} levelName={levelName} heading={summaries.length > 1 ? entities[s.entity].label : null} />
      ))}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8, position: 'sticky', bottom: 72, background: 'var(--canvas)', padding: '12px 0' }}>
        <span className="caption grow">{totals.unchangedCount === 1 ? '1 unchanged row is not shown.' : `${totals.unchangedCount.toLocaleString('en-US')} unchanged rows are not shown.`}</span>
        <button className="btn" type="button" onClick={cancel} disabled={busy === 'apply'}>
          Cancel
        </button>
        <button className="btn btn-primary" type="button" onClick={apply} disabled={busy != null || totals.changes === 0}>
          {busy === 'apply' ? 'Importing…' : importButtonLabel(totals.changes, totals.errorCount)}
        </button>
      </div>
    </div>
  );
}

function FileBadge() {
  return (
    <span style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--accent-soft)', color: 'var(--accent-ink)', display: 'grid', placeItems: 'center' }}>
      <Icon name="file" />
    </span>
  );
}

const fieldLabel = (field, levelName) => (field === 'Hierarchy Node' ? levelName : field);

function ReportPreview({ entity, summary, levelName, heading }) {
  const { created, updated, errors } = summary;
  const createdCard = created.length > 0 && (
    <section className={`card ${errors.length ? 'c-6' : 'c-12'}`}>
      <div className="card-head">
        <div>
          <div className="t">Will be created</div>
        </div>
      </div>
      <div className="card-body flush">
        <table className="table compact">
          <thead>
            <tr>
              {entity.createdColumns.map((c) => (
                <th key={c.key} className={c.numeric ? 'n' : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {created.map((row) => (
              <tr key={row.rowNumber}>
                {entity.createdColumns.map((c) => {
                  const value = (row.values || {})[c.key] || (c.fallbackKey ? (row.values || {})[c.fallbackKey] : null) || (c.title ? row.label : '');
                  if (c.title) {
                    return (
                      <td key={c.key} className="cell-title">
                        {value}
                      </td>
                    );
                  }
                  if (c.tags) {
                    const tags = splitTags(value);
                    return (
                      <td key={c.key}>
                        {tags.length ? (
                          <div className="row-wrap" style={{ gap: 4 }}>
                            {tags.map((t) => (
                              <span className="tag" key={t}>
                                {t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="caption">None</span>
                        )}
                      </td>
                    );
                  }
                  return (
                    <td key={c.key} className={c.numeric ? 'n' : undefined}>
                      {value || <span className="caption">None</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  const errorCard = errors.length > 0 && (
    <section className={`card ${created.length ? 'c-6' : 'c-12'}`}>
      <div className="card-head">
        <div>
          <div className="t">Errors</div>
          <div className="d">Fix these rows in the file and upload again, or import without them</div>
        </div>
      </div>
      <div className="card-body flush">
        <table className="table compact">
          <thead>
            <tr>
              <th className="tight">Row</th>
              <th>Name</th>
              <th>Problem</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e) => (
              <tr key={`${e.rowNumber}-${e.message}`}>
                <td className="num">{e.rowNumber || '—'}</td>
                <td className="cell-title">{e.label || '(blank)'}</td>
                <td>
                  <span className="field-error">
                    <Icon name="alert" className="ico-sm" />
                    {e.message}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  const updatedCard = updated.length > 0 && (
    <section className="card c-12">
      <div className="card-head">
        <div>
          <div className="t">Will be updated</div>
          <div className="d">Current value, then the value from the file</div>
        </div>
      </div>
      <div className="card-body flush">
        <table className="table compact">
          <thead>
            <tr>
              <th>{entity.rowHeader}</th>
              <th>Field</th>
              <th>Now</th>
              <th>After import</th>
            </tr>
          </thead>
          <tbody>
            {updated.map((row) =>
              (row.fieldChanges || []).map((change, i) => (
                <tr key={`${row.rowNumber}-${change.field}`}>
                  {i === 0 && (
                    <td className="cell-title" rowSpan={row.fieldChanges.length} style={{ verticalAlign: 'top' }}>
                      {row.label}
                    </td>
                  )}
                  <td className="small muted">
                    {fieldLabel(change.field, levelName)}
                    {i === 0 && row.matchedOn === 'alias' ? ' (matched by alias)' : ''}
                  </td>
                  <td className="small diff-before">{change.before || 'None'}</td>
                  <td className="small">
                    <span className="diff-after">{change.after || 'None'}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <>
      {heading && <h2 className="section-title">{heading}</h2>}
      <section className="kpis">
        <div className="kpi">
          <div className="k-label">
            <Icon name="plus" className="ico-sm" />
            Will be created
          </div>
          <div className="k-row">
            <div className="k-value">{summary.createdCount.toLocaleString('en-US')}</div>
          </div>
          <div className="k-meta">new {entity.nouns}</div>
        </div>
        <div className="kpi">
          <div className="k-label">
            <Icon name="edit" className="ico-sm" />
            Will be updated
          </div>
          <div className="k-row">
            <div className="k-value">{summary.updatedCount.toLocaleString('en-US')}</div>
          </div>
          <div className="k-meta">{plural(summary.fieldChanges, 'field change')}</div>
        </div>
        <div className="kpi">
          <div className="k-label">
            <Icon name="check" className="ico-sm" />
            Unchanged
          </div>
          <div className="k-row">
            <div className="k-value">{summary.unchangedCount.toLocaleString('en-US')}</div>
          </div>
          <div className="k-meta">already match</div>
        </div>
        <div className="kpi">
          <div className="k-label" style={errors.length ? { color: 'var(--critical)' } : undefined}>
            <Icon name="alert" className="ico-sm" />
            Errors
          </div>
          <div className="k-row">
            <div className="k-value" style={errors.length ? { color: 'var(--critical)' } : undefined}>
              {errors.length.toLocaleString('en-US')}
            </div>
          </div>
          <div className="k-meta">{errors.length ? 'rows skipped until fixed' : 'none'}</div>
        </div>
      </section>
      {(createdCard || errorCard || updatedCard) && (
        <div className="grid">
          {createdCard}
          {errorCard}
          {updatedCard}
        </div>
      )}
    </>
  );
}
